import React from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import { useLandpageSettings } from '../../hooks/useLandpageSettings';
import { Zap, Sun, ShieldAlert, TrendingDown, ArrowRight, CheckCircle2, BatteryCharging, Award } from 'lucide-react';

export const Hero: React.FC = () => {
  const { setActiveTab, setIsLeadModalOpen, petrolPrice, gridTariff } = useSolarStore();
  const { settings } = useLandpageSettings();

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-900 text-white pt-12 pb-20 border-b border-slate-800">
      {/* Background glow effects */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-emerald-600/15 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute top-10 right-10 w-72 h-72 bg-teal-500/10 blur-[100px] rounded-full pointer-events-none" />

      <div className="w-full max-w-none px-4 md:px-8 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Value Prop */}
          <div className="lg:col-span-7 space-y-6 text-left">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold tracking-wider uppercase">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
              Nigeria's #1 Smart Solar & Power Transition Platform
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1]">
              {settings.headline} <br />
              <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 bg-clip-text text-transparent">
                {settings.subheadline}
              </span>
            </h1>

            <p className="text-lg text-slate-300 max-w-2xl leading-relaxed">
              With petrol at <strong className="text-white">₦{petrolPrice.toLocaleString()}/L</strong> and Band A electricity tariffs surging to <strong className="text-white">₦{gridTariff}/kWh</strong>, reliance on fossil fuel generators drains your income. Access Tech delivers engineered Tier-1 solar systems with guaranteed 5-year support.
            </p>

            <div className="flex flex-wrap gap-4 pt-2">
              <button
                onClick={() => setActiveTab('calculator')}
                className="flex items-center gap-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-7 py-4 rounded-xl shadow-xl shadow-emerald-500/25 transition-all transform hover:-translate-y-0.5 text-base"
              >
                Calculate Your Custom Solar System
                <ArrowRight className="w-5 h-5" />
              </button>

              <button
                onClick={() => setIsLeadModalOpen(true, 'Request Instant Site Survey')}
                className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white font-semibold px-6 py-4 rounded-xl border border-slate-700 transition-all text-base"
              >
                Request Free Site Survey
              </button>
            </div>

            {/* Key trust badges */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-6 border-t border-slate-800 text-sm">
              <div className="flex items-center gap-2.5 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <span>6,000+ LifePO4 Cycles</span>
              </div>
              <div className="flex items-center gap-2.5 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <span>Zero Noise & Zero Fuel</span>
              </div>
              <div className="flex items-center gap-2.5 text-slate-300">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                <span>25-Year Panel Warranty</span>
              </div>
            </div>
          </div>

          {/* Right Column: Live Impact Card */}
          <div className="lg:col-span-5">
            <div className="bg-slate-900/90 rounded-3xl p-6 sm:p-8 border border-emerald-500/30 shadow-2xl relative backdrop-blur-xl">
              <div className="absolute -top-3 -right-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3.5 py-1 rounded-full shadow-lg flex items-center gap-1">
                <Award className="w-3.5 h-3.5" /> Verified ROI
              </div>

              <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <BatteryCharging className="w-6 h-6 text-emerald-400" />
                Nigerian Energy Impact Live
              </h3>
              <p className="text-xs text-slate-400 mb-6">Real-time comparison between fossil generator vs Access Tech Solar system.</p>

              <div className="space-y-4">
                <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-slate-400 block font-medium">Monthly Generator Fuel Burn</span>
                    <span className="text-lg font-bold text-rose-400">₦285,000 /mo</span>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center text-rose-400">
                    <TrendingDown className="w-5 h-5" />
                  </div>
                </div>

                <div className="bg-emerald-950/40 p-4 rounded-2xl border border-emerald-500/30 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-emerald-300 block font-medium">Access Tech Solar Savings</span>
                    <span className="text-2xl font-extrabold text-emerald-400">₦220,000+ /mo</span>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 shadow-lg">
                    <Sun className="w-6 h-6 animate-spin-slow" />
                  </div>
                </div>

                <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2">
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>Average System Payback</span>
                    <span className="text-emerald-400 font-bold">13 - 14 Months</span>
                  </div>
                  <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-400 h-full w-[85%]" />
                  </div>
                </div>
              </div>

              <button
                onClick={() => setActiveTab('packages')}
                className="w-full mt-6 bg-slate-800 hover:bg-slate-700 text-emerald-400 font-semibold py-3 rounded-xl border border-emerald-500/30 transition-all text-sm flex items-center justify-center gap-2"
              >
                View 1.5kVA, 3.5kVA & 7.5kVA Packages
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
